import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "@/api";
import { errorMessage } from "@/api/client";
import type { WatchedFolder } from "@/api/types";
import {
  Button,
  Card,
  ConfirmDialog,
  ErrorBanner,
  Field,
  inputClass,
  PageHeader,
  Spinner,
} from "@/components/ui";
import { formatDateTime } from "@/lib/dates";

export default function FoldersPage() {
  const qc = useQueryClient();
  const [boxFolderId, setBoxFolderId] = useState("");
  const [name, setName] = useState("");
  const [removing, setRemoving] = useState<WatchedFolder | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const folders = useQuery({ queryKey: ["folders"], queryFn: api.listFolders });
  const refresh = () => qc.invalidateQueries({ queryKey: ["folders"] });

  const add = useMutation({
    mutationFn: () =>
      api.addFolder({ boxFolderId: boxFolderId.trim(), name: name.trim() }),
    onSuccess: () => {
      setBoxFolderId("");
      setName("");
      return refresh();
    },
  });
  const toggle = useMutation({
    mutationFn: (f: WatchedFolder) => api.setFolderEnabled(f.id, !f.enabled),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.deleteFolder(id),
    onSuccess: () => {
      setRemoving(null);
      return refresh();
    },
  });
  const crawl = useMutation({
    mutationFn: (f: WatchedFolder) => api.crawlFolder(f.id),
    onSuccess: (_, f) => setNotice(`Crawl queued for ${f.name}.`),
  });

  const err = [
    folders.error,
    add.error,
    toggle.error,
    remove.error,
    crawl.error,
  ].find(Boolean);

  return (
    <>
      <PageHeader title="Watched Folders" />
      <ErrorBanner message={err ? errorMessage(err) : null} />
      {notice && (
        <p className="mb-4 rounded bg-green-50 p-3 text-sm text-green-800">
          {notice}
        </p>
      )}
      <Card title="Add folder">
        <form
          className="grid items-end gap-4 md:grid-cols-3"
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate();
          }}
        >
          <Field label="Box folder ID">
            <input
              className={inputClass}
              value={boxFolderId}
              onChange={(e) => setBoxFolderId(e.target.value)}
            />
          </Field>
          <Field label="Name">
            <input
              className={inputClass}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <div>
            <Button
              type="submit"
              disabled={!boxFolderId.trim() || !name.trim() || add.isPending}
            >
              Add
            </Button>
          </div>
        </form>
      </Card>

      <div className="mt-6">
        {folders.isPending ? (
          <Spinner />
        ) : (
          <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
            <table className="w-full text-left text-sm">
              <thead className="bg-neutral-100 text-neutral-600">
                <tr>
                  <th className="p-3">Name</th>
                  <th className="p-3">Box ID</th>
                  <th className="p-3">Added</th>
                  <th className="p-3">Last crawl</th>
                  <th className="p-3">Enabled</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {folders.data?.map((f) => (
                  <tr key={f.id} className="border-t border-neutral-200">
                    <td className="p-3 font-medium">{f.name}</td>
                    <td className="p-3 font-mono">{f.id}</td>
                    <td className="p-3">{formatDateTime(f.addedAt)}</td>
                    <td className="p-3">{formatDateTime(f.lastCrawlAt)}</td>
                    <td className="p-3">
                      <input
                        type="checkbox"
                        aria-label={`Enable ${f.name}`}
                        checked={f.enabled}
                        disabled={toggle.isPending}
                        onChange={() => toggle.mutate(f)}
                      />
                    </td>
                    <td className="flex justify-end gap-2 p-3">
                      <Button
                        variant="secondary"
                        disabled={crawl.isPending}
                        onClick={() => {
                          setNotice(null);
                          crawl.mutate(f);
                        }}
                      >
                        Crawl
                      </Button>
                      <Button variant="danger" onClick={() => setRemoving(f)}>
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
                {folders.data?.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="p-6 text-center text-neutral-500"
                    >
                      No watched folders yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {removing && (
        <ConfirmDialog
          title="Remove folder?"
          message={`Stop watching "${removing.name}" (${removing.id})?`}
          confirmLabel="Remove"
          danger
          busy={remove.isPending}
          onConfirm={() => remove.mutate(removing.id)}
          onCancel={() => setRemoving(null)}
        />
      )}
    </>
  );
}
